import React from 'react';
import { useTranslation } from 'react-i18next';
import LoadingCard from '../../../../components/loadingCard';
import { Icon, Status, Card, SelectHierarchyLabel } from '@weezevent/nacre';
import { InfoTextTooltip } from '../../../attendee/detail/components/infotext-tooltip/infoTextTooltip';
import css from './picker.module.css';

export default function EventsPicker({
    form,
    label = null,
    events = [],
    inventories = [],
    loadingEvents,
    loadingInventories,
    query,
    handleSearch = () => {},
    onChangeInventories,
    onChangeEvents,
    virtual = false,
    useInventories = false,
    eventsDisplay = false,
    inventoriesDisplay = false,
    edition = false,
    disabled = false
}) {
    const { t } = useTranslation();

    const selectedEvents = React.useMemo(() => {
        if (!events) {
            return [];
        }
        let _selectedEvents = events?.filter(ev => form.events.includes(ev.id));

        return _selectedEvents;
    }, [form, events]);

    const selectedInventories = React.useMemo(() => {
        if (!inventories) {
            return [];
        }
        let _selectedInventories = inventories?.filter(iv => form.sub_inventories.includes(iv.uuid));
        return _selectedInventories;
    }, [form, inventories]);

    const eventsList = React.useMemo(() => {
        if (!query || query === '') {
            return events.filter(ev => !form.events?.includes(ev.id));
        }
        const reg = new RegExp(query, 'gi');
        return events?.filter(ev => ev.name.match(reg) && !form.events?.includes(ev.id));
    }, [events, form, query]);

    const inventoriesList = React.useMemo(() => {
        return inventories?.filter(it => !form.sub_inventories?.includes(it.uuid));
    }, [inventories, form]);

    const dateFormat = React.useCallback(item => {
        return `${new Date(item?.start_date).toLocaleDateString([], {
            weekday: 'short',
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            timeZone: item?.timezone
        })}, ${new Date(item?.start_date).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: item?.timezone
        })} `;
    });

    const selectEvent = React.useCallback(
        eventId => {
            let newSelectedEvents = virtual ? new Set() : new Set(form.events);
            newSelectedEvents.add(eventId);

            onChangeEvents(Array.from(newSelectedEvents));
        },
        [form, virtual, onChangeEvents]
    );

    const unselectEvent = React.useCallback(
        event => {
            let newSelectedEvents = new Set(form.events);
            newSelectedEvents.delete(event.id);
            onChangeEvents(Array.from(newSelectedEvents));
        },
        [form, onChangeEvents]
    );

    const handleDelete = React.useCallback(
        name => {
            if (name === 'inventories') {
                onChangeInventories([]);
            }
            if (name === 'events') {
                onChangeEvents([]);
            }
        },
        [onChangeInventories, onChangeEvents]
    );

    const selectInventory = React.useCallback(
        inventoryId => {
            let newSelectedInventories = new Set(form.sub_inventories);
            newSelectedInventories.add(inventoryId);
            onChangeInventories(Array.from(newSelectedInventories));
        },
        [form, onChangeInventories]
    );

    const unselectInventory = React.useCallback(
        inventory => {
            let newSelectedInventories = new Set(form.sub_inventories);
            newSelectedInventories.delete(inventory.uuid);
            onChangeInventories(Array.from(newSelectedInventories));
        },
        [form, onChangeInventories]
    );

    const inventoryEvent = React.useCallback(
        inventory => {
            return events?.find(ev => ev.id === inventory.event);
        },
        [events]
    );

    const getColorStatus = React.useCallback(
        (item, name) => {
            let status;
            if (name === 'events') {
                status = item?.publication_status;
                if (status === 'draft') {
                    return 'orange';
                }
                if (status === 'closed') {
                    return 'red';
                }
                return 'green';
            }

            if (name === 'inventories') {
                status = inventoryEvent(item)?.publication_status;
                return status === 'draft' ? 'orange' : 'green';
            }
            return null;
        },
        [inventoryEvent]
    );

    const generateOptions = React.useCallback(
        (items, name) => {
            return items.map(i => ({
                text: (
                    <div className={css['events-picker-options']}>
                        <InfoTextTooltip title={i.name} subtitle={dateFormat(name === 'events' ? i : inventoryEvent(i))} />
                        <div>
                            <Status
                                responsive
                                text={t(`ticket.rate_template.${name === 'events' ? i?.publication_status : inventoryEvent(i)?.publication_status}`)}
                                color={getColorStatus(i, name)}
                            />
                        </div>
                    </div>
                ),
                value: name === 'events' ? i.id : i.uuid
            }));
        },
        [dateFormat, inventoryEvent, getColorStatus]
    );

    return (
        <div className={css['event-picker']}>
            <SelectedList
                label={label}
                item={eventsList}
                name="events"
                edition={edition}
                query={query}
                select={selectEvent}
                options={generateOptions}
                dateFormat={dateFormat}
                loadingEvents={loadingEvents}
                loadingInventories={loadingInventories}
                selected={selectedEvents}
                handleDelete={handleDelete}
                handleSearch={handleSearch}
                unselect={unselectEvent}
                generator={generateOptions}
                getColor={getColorStatus}
                virtual={virtual}
                display={eventsDisplay}
                disabled={disabled}
            />

            {virtual && (
                <SelectedList
                    label={label}
                    item={inventoriesList}
                    name="inventories"
                    edition={edition}
                    query={query}
                    select={selectInventory}
                    dateFormat={dateFormat}
                    loadingEvents={loadingEvents}
                    loadingInventories={loadingInventories}
                    finder={inventoryEvent}
                    selected={selectedInventories}
                    handleDelete={handleDelete}
                    unselect={unselectInventory}
                    generator={generateOptions}
                    getColor={getColorStatus}
                    virtual={virtual}
                    useInventories={useInventories}
                    display={inventoriesDisplay}
                    disabled={disabled}
                />
            )}
        </div>
    );
}

export function SelectedList({
    item,
    name,
    label,
    edition,
    query,
    select,
    handleDelete,
    loadingEvents,
    loadingInventories,
    unselect,
    selected,
    generator,
    handleSearch,
    dateFormat,
    finder = () => {},
    getColor,
    virtual,
    useInventories,
    display,
    disabled
}) {
    const { t } = useTranslation();
    const [hiddenSatusText, setHiddenStatusText] = React.useState(false);

    React.useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setHiddenStatusText(width >= 900 && width <= 1100);
        };

        window.addEventListener('resize', handleResize);

        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const loadingOptions = React.useMemo(() => {
        return loadingEvents || loadingInventories;
    }, [loadingEvents, loadingInventories]);

    const selectedCount = React.useMemo(() => {
        if (!selected.length) {
            return t('ticket.common.action.select');
        }
        return t(`ticket.charts.chart.${name}_selected`, { count: selected.length });
    }, [selected, name]);

    const handleCardDelete = React.useCallback(
        item => {
            if (name === 'events' && edition) {
                return null;
            }
            return unselect(item);
        },
        [name, edition]
    );

    const dropdownLabel = React.useMemo(() => {
        if (label && label.length) return label;
        if (name === 'events' && virtual) {
            return t('ticket.charts.chart.inventories_events_virtual');
        }
        return t(`ticket.charts.${name === 'events' ? 'chart.inventories_events' : 'inventory.subInventories'}`);
    }, [name, virtual, label]);

    return (
        <LoadingCard loading={loadingOptions} height={edition ? '239px' : '115px'}>
            <div className={css['selector-container']}>
                <SelectHierarchyLabel
                    className={css['events-selector']}
                    label={dropdownLabel}
                    onChange={(synth, data) => select(data.value)}
                    externalOptions={generator(item, name)}
                    options={generator(item, name)}
                    onExternalSearch={handleSearch}
                    placeholder={selectedCount}
                    placeholderSearch={t('ticket.common.action.search')}
                    disabled={(name === 'events' && edition) || disabled}
                    selected={selected}
                    searchValue={query}
                    searchable={name === 'events'}
                    height="medium"
                />
            </div>

            {display && selected.length > 0 && (
                <div className={css['events-container']}>
                    {(!virtual || (virtual && useInventories)) && selected.length > 1 && (
                        <div className={`${css['inventory-event-card-content']} ${css['delete-all']}`}>
                            <span>{t(`ticket.charts.chart.${name === 'events' ? 'inventories_events_list' : 'subInventories_events_list'}`)}</span>
                            <span>
                                <span className={css['inventory-delete-label']} onClick={() => handleDelete(name)}>
                                    {t(`ticket.charts.chart.${name === 'events' ? 'inventories_events_del' : 'subInventories_events_del'}`)}
                                </span>
                                <Icon name="trash" onClick={() => handleDelete(name)} />
                            </span>
                        </div>
                    )}

                    {selected.map(item => (
                        <Card key={item.id} className={css['inventory-event-card']} onClick={() => handleCardDelete(item)}>
                            <div className={`${css['inventory-event-card-content']} ${css['card-size']}`}>
                                <div className={css['card-content-text']}>
                                    <InfoTextTooltip title={item.name} subtitle={dateFormat(name === 'events' ? item : finder(item))} />
                                </div>
                                <div className={css['event-card-status']}>
                                    <Status
                                        responsive
                                        text={hiddenSatusText ? '' : t(`ticket.rate_template.${name === 'events' ? item?.publication_status : finder(item)?.publication_status}`)}
                                        color={getColor(item, name)}
                                    />
                                    {name === 'events' && edition ? null : <Icon className={css['event-card-delete']} name="cross" onClick={() => unselect(item)} />}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </LoadingCard>
    );
}
